import type {CSSProperties} from 'react';
import {forwardRef, memo} from 'react';

import type {SizeUpTo} from '#theme';
import {BaseColor, colorVarMap, getVariantColors, Size} from '#theme';

import * as classes from './style.module.scss';

const SPINNER_NAME = 'Spinner';

const sizeToPixels = {
  [Size.One]: 12,
  [Size.Two]: 18,
  [Size.Three]: 24,
  [Size.Four]: 30,
  [Size.Five]: 36,
  [Size.Six]: 42,
  [Size.Seven]: 48,
  [Size.Eight]: 54,
};

export interface SpinnerProps {
  size?: SizeUpTo<8>;
  speed?: number;
  color?: BaseColor;
  ariaLabel?: string;
}

const findTrackColor = (color: BaseColor) => {
  const colorMapping = getVariantColors(color);

  return colorVarMap[colorMapping[0].variant];
};

const findSizeToPixels = (size: SizeUpTo<8>) => {
  if (size > 8) {
    return sizeToPixels[8];
  }

  return sizeToPixels[size];
};

export const Spinner = memo(
  forwardRef((props: SpinnerProps, ref) => {
    const {
      size = Size.Three,
      speed = 1,
      color = BaseColor.Primary,
      ariaLabel,
    } = props;

    const trackColor = findTrackColor(color);
    const pixelSize = findSizeToPixels(size);

    const viewBox = '0 0 50 50 ';
    const center = '25';
    const radius = '20';

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={classes.spinner}
        role="status"
        aria-label={ariaLabel}
        style={
          {
            '--size': `${pixelSize}px`,
            animationDuration: `${speed}s`,
          } as CSSProperties
        }
      >
        <svg className={classes.spinner__svg} viewBox={viewBox}>
          <circle
            className={classes.spinner__track}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            style={{
              stroke: trackColor,
            }}
          />
          <circle
            className={classes.spinner__circle}
            cx={center}
            cy={center}
            r={radius}
            style={{
              stroke: colorVarMap[color],
            }}
          />
        </svg>
      </div>
    );
  })
);

Spinner.displayName = SPINNER_NAME;
